import React from "react";
import { useTranslation } from "react-i18next";

// Wallet event-ticket strip is 1125x294 (3x): keep in sync with the output size in DigitalCardImageEditor.
const STRIP_RATIO = "1125 / 294";
const SAMPLE_QR = "/sample_qr.svg";
const NOTCH = 18;

// Generic mock of an Apple Wallet event ticket, so organisers can see where their image lands.
// `notchColor` should match the surface the card sits on (the cut-outs are drawn, not masked).
// `hideTicketType` drops the TICKET/Regular badge, matching passes for social/marriage events.
const DigitalCardPreview = ({ imageUrl, title, dateText, venue, logoUrl, color = "#1e3a8a", notchColor = "#fff", hideTicketType = false }) => {
  const { t } = useTranslation();

  const label = { fontSize: 9, letterSpacing: 0.5, textTransform: "uppercase", opacity: 0.7, whiteSpace: "nowrap" };
  const value = { fontSize: 13, fontWeight: 600, lineHeight: 1.3 };
  const notch = (side) => ({
    position: "absolute",
    width: NOTCH,
    height: NOTCH,
    borderRadius: "50%",
    background: notchColor,
    top: "50%",
    marginTop: -NOTCH / 2,
    [side]: -NOTCH / 2,
  });

  return (
    <div
      style={{
        position: "relative",
        maxWidth: 300,
        margin: "0 auto",
        borderRadius: 20,
        overflow: "hidden",
        color: "#fff",
        background: color,
        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
      }}
    >
      {/* Punch-hole at the top, like a physical ticket / wallet pass */}
      <div style={{ position: "absolute", top: -NOTCH / 2, left: "50%", marginLeft: -NOTCH / 2, width: NOTCH, height: NOTCH, borderRadius: "50%", background: notchColor, zIndex: 2 }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "20px 16px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ height: 26, maxWidth: 64, objectFit: "contain" }} />}
          <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {title || t("eventForm.passImage.sampleTitle")}
          </span>
        </div>
        {!hideTicketType && (
          <div style={{ textAlign: "end", flexShrink: 0 }}>
            <div style={label}>{t("eventForm.passImage.ticket")}</div>
            <div style={value}>{t("eventForm.passImage.regular")}</div>
          </div>
        )}
      </div>

      <div
        style={{
          width: "100%",
          aspectRatio: STRIP_RATIO,
          background: imageUrl ? `url(${imageUrl}) center / cover` : "rgba(255,255,255,0.15)",
          position: "relative",
        }}
      >
        {/* The server adds a dark gradient over the image so the text stays readable. */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.45))" }} />
        {!imageUrl && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, opacity: 0.85, padding: "0 12px", textAlign: "center" }}>
            {t("eventForm.passImage.noImage")}
          </div>
        )}
      </div>

      <div style={{ padding: "12px 16px 4px" }}>
        <div style={label}>{t("eventForm.passImage.attendee")}</div>
        <div style={value}>{t("eventForm.passImage.sampleGuest")}</div>
      </div>
      <div style={{ display: "flex", gap: 16, padding: "8px 16px 14px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={label}>{t("eventForm.passImage.date")}</div>
          <div style={value}>{dateText || "—"}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={label}>{t("eventForm.passImage.venue")}</div>
          <div style={value}>{venue || "—"}</div>
        </div>
      </div>

      {/* Tear-off line with side cut-outs */}
      <div style={{ position: "relative", height: 0 }}>
        <div style={{ borderTop: "2px dashed rgba(255,255,255,0.35)", margin: "0 18px" }} />
        <div style={notch("left")} />
        <div style={notch("right")} />
      </div>

      <div style={{ padding: "22px 16px 18px" }}>
        <div style={{ background: "#fff", borderRadius: 12, padding: 12, display: "flex", justifyContent: "center" }}>
          <img src={SAMPLE_QR} alt="QR" style={{ width: 110, height: 110 }} />
        </div>
      </div>
    </div>
  );
};

export default DigitalCardPreview;
